import { FormEvent, useEffect, useMemo, useRef, useState } from "react";

import { PcmCapture } from "./audio/pcmCapture";
import { PcmPlayer } from "./audio/pcmPlayer";
import type { AuthUser } from "./authTypes";
import type {
  DialogMessage,
  MemoryContextSummary,
  ServerEvent,
  SessionPhase,
} from "./protocol";

const phaseLabels: Record<SessionPhase, string> = {
  connecting: "Подключаем стенд",
  ready: "Готова к тесту",
  listening: "Слушаю клиента",
  thinking: "Обдумываю ответ",
  speaking: "Отвечаю",
  error: "Нужна проверка",
};

const makeId = () => crypto.randomUUID();

type AudioTuning = {
  sensitivity: number;
  speechSpeed: number;
  voiceVolume: number;
  interruptionProtection: number;
};

const defaultAudioTuning: AudioTuning = {
  sensitivity: 35,
  speechSpeed: 50,
  voiceVolume: 82,
  interruptionProtection: 65,
};

const loadAudioTuning = (): AudioTuning => {
  try {
    const saved = JSON.parse(localStorage.getItem("alena-audio-tuning") ?? "{}") as
      Partial<AudioTuning>;
    return Object.fromEntries(
      Object.entries(defaultAudioTuning).map(([key, fallback]) => {
        const candidate = Number(saved[key as keyof AudioTuning]);
        return [key, Number.isFinite(candidate) ? Math.max(1, Math.min(100, candidate)) : fallback];
      }),
    ) as AudioTuning;
  } catch {
    return defaultAudioTuning;
  }
};

const tuningControls: {
  key: keyof AudioTuning;
  label: string;
  hint: string;
}[] = [
  {
    key: "sensitivity",
    label: "Чувствительность к звукам",
    hint: "Ниже — лучше отсекает фон; выше — слышит более тихую речь.",
  },
  {
    key: "interruptionProtection",
    label: "Защита от случайного перебивания",
    hint: "Выше — шум должен звучать дольше, прежде чем Алёна замолчит.",
  },
  {
    key: "speechSpeed",
    label: "Скорость речи",
    hint: "Ниже — медленнее; выше — быстрее. Новый темп применяется со следующей реплики.",
  },
  {
    key: "voiceVolume",
    label: "Громкость голоса",
    hint: "Громкость Алёны в этом браузере.",
  },
];

type AgentWorkspaceProps = {
  csrfToken: string;
  user: AuthUser;
  onLogout: () => void;
  onOpenAdmin: () => void;
  onSessionExpired: () => void;
};

export function AgentWorkspace({
  csrfToken,
  user,
  onLogout,
  onOpenAdmin,
  onSessionExpired,
}: AgentWorkspaceProps) {
  const [phase, setPhase] = useState<SessionPhase>("connecting");
  const [mode, setMode] = useState<"live" | "demo">("demo");
  const [messages, setMessages] = useState<DialogMessage[]>([]);
  const [partial, setPartial] = useState("");
  const [notice, setNotice] = useState("");
  const [text, setText] = useState("");
  const [micLevel, setMicLevel] = useState(0);
  const [isMicOn, setIsMicOn] = useState(false);
  const [ttsOptions, setTtsOptions] = useState<{ id: string; label: string }[]>([]);
  const [ttsEngine, setTtsEngine] = useState("");
  const [ttsLabel, setTtsLabel] = useState("Голос");
  const [reactionMs, setReactionMs] = useState<number | null>(null);
  const [interruptions, setInterruptions] = useState(0);
  const [openingStyle, setOpeningStyle] = useState<"partnership" | "project">(
    "partnership",
  );
  const [feedbackMessageId, setFeedbackMessageId] = useState<string | null>(null);
  const [feedbackTags, setFeedbackTags] = useState<string[]>([]);
  const [feedbackCorrection, setFeedbackCorrection] = useState("");
  const [feedbackPending, setFeedbackPending] = useState(false);
  const [feedbackRecording, setFeedbackRecording] = useState(false);
  const [feedbackRecordingId, setFeedbackRecordingId] = useState<string | null>(null);
  const [feedbackPartial, setFeedbackPartial] = useState("");
  const [feedbackAudioDurationMs, setFeedbackAudioDurationMs] = useState(0);
  const [memoryContexts, setMemoryContexts] = useState<MemoryContextSummary[]>([]);
  const [selectedContactId, setSelectedContactId] = useState("");
  const [selectedDealId, setSelectedDealId] = useState("");
  const [memoryOrganization, setMemoryOrganization] = useState("");
  const [memoryContact, setMemoryContact] = useState("");
  const [memoryPhone, setMemoryPhone] = useState("");
  const [memoryRole, setMemoryRole] = useState("");
  const [memoryDeal, setMemoryDeal] = useState("");
  const [newDealTitle, setNewDealTitle] = useState("");
  const [memoryPending, setMemoryPending] = useState(false);
  const [loadedMemoryLabel, setLoadedMemoryLabel] = useState("");
  const [conversationReview, setConversationReview] = useState<{
    outcome: "success" | "partial" | "failure" | "unclear";
    score: number;
    issues: { code: string; detail: string }[];
    summary: string;
  } | null>(null);
  const [audioTuning, setAudioTuning] = useState<AudioTuning>(loadAudioTuning);
  const conversationIdRef = useRef(crypto.randomUUID());

  const socketRef = useRef<WebSocket | null>(null);
  const captureRef = useRef<PcmCapture | null>(null);
  const playerRef = useRef(new PcmPlayer());
  const sttRateRef = useRef(16_000);
  const ttsRateRef = useRef(24_000);
  const modeRef = useRef<"live" | "demo">("demo");
  const phaseRef = useRef<SessionPhase>("connecting");
  const micOnRef = useRef(false);
  const currentAgentIdRef = useRef<string | null>(null);
  const userFinishedAtRef = useRef<number | null>(null);
  const gotFirstDeltaRef = useRef(false);
  const mountedRef = useRef(true);
  const feedbackStartedMicrophoneRef = useRef(false);
  const ttsLabelRef = useRef("Голос");
  const memoryBoundConversationRef = useRef<string | null>(null);
  const audioTuningRef = useRef(audioTuning);

  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  useEffect(() => {
    micOnRef.current = isMicOn;
  }, [isMicOn]);

  const send = (payload: object) => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify(payload));
    }
  };

  useEffect(() => {
    audioTuningRef.current = audioTuning;
    localStorage.setItem("alena-audio-tuning", JSON.stringify(audioTuning));
    playerRef.current.setVolume(audioTuning.voiceVolume);
    captureRef.current?.setSensitivity(audioTuning.sensitivity);
    captureRef.current?.setInterruptionProtection(
      audioTuning.interruptionProtection,
    );
    send({ type: "agent.settings.set", speechSpeed: audioTuning.speechSpeed });
  }, [audioTuning]);

  const loadMemoryContexts = async () => {
    try {
      const response = await fetch("/api/memory/contexts", {
        credentials: "same-origin",
      });
      if (response.status === 401) {
        onSessionExpired();
        return;
      }
      if (!response.ok) throw new Error("memory_list_failed");
      const payload = (await response.json()) as {
        contexts: MemoryContextSummary[];
      };
      setMemoryContexts(payload.contexts);
    } catch {
      setNotice("Не удалось загрузить память клиентов.");
    }
  };

  useEffect(() => {
    void loadMemoryContexts();
    // Список памяти загружается один раз после входа.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    const socketUrl = new URL("ws/voice", document.baseURI);
    socketUrl.protocol = location.protocol === "https:" ? "wss:" : "ws:";
    const socket = new WebSocket(socketUrl);
    socket.binaryType = "arraybuffer";
    socketRef.current = socket;

    socket.onmessage = (message) => {
      if (message.data instanceof ArrayBuffer) {
        void playerRef.current.enqueue(message.data, ttsRateRef.current);
        return;
      }
      handleServerEvent(JSON.parse(message.data) as ServerEvent);
    };
    socket.onerror = () => {
      setNotice("Не удалось подключиться к серверу агента.");
      setPhase("error");
    };
    socket.onclose = (event) => {
      if (mountedRef.current) {
        if (event.code === 4401) {
          onSessionExpired();
          return;
        }
        setNotice("Соединение с сервером закрыто. Обновите страницу.");
        setPhase("error");
      }
    };

    return () => {
      mountedRef.current = false;
      socket.close();
      void captureRef.current?.stop();
      void playerRef.current.close();
    };
    // Подключение создаётся один раз; обработчик использует функциональные обновления.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleServerEvent = (event: ServerEvent) => {
    switch (event.type) {
      case "session.ready":
        setMode(event.mode);
        sttRateRef.current = event.sttSampleRate;
        ttsRateRef.current = event.ttsSampleRate;
        setTtsOptions(event.ttsOptions);
        setTtsEngine(event.ttsEngine);
        setTtsLabel(event.voice);
        ttsLabelRef.current = event.voice;
        send({
          type: "agent.settings.set",
          speechSpeed: audioTuningRef.current.speechSpeed,
        });
        setPhase("ready");
        if (event.mode === "demo") {
          setNotice(
            "Сейчас работает текстовый деморежим. После ключа Yandex Cloud включатся микрофон и голос SpeechKit.",
          );
        }
        break;
      case "agent.settings.changed":
        break;
      case "conversation.reviewed":
        setConversationReview({
          outcome: event.outcome,
          score: event.score,
          issues: event.issues,
          summary: event.summary,
        });
        break;
      case "tts.engine.changed":
        setTtsEngine(event.engine);
        setTtsLabel(event.label);
        ttsLabelRef.current = event.label;
        ttsRateRef.current = event.sampleRate;
        setNotice("");
        setPhase(micOnRef.current ? "listening" : "ready");
        break;
      case "voice.ready":
        setPhase("listening");
        break;
      case "session.notice":
        setNotice(event.message);
        break;
      case "session.error":
        setNotice(event.message);
        setPhase("error");
        break;
      case "session.reset":
        setMessages([]);
        setPartial("");
        setReactionMs(null);
        setInterruptions(0);
        memoryBoundConversationRef.current = null;
        setLoadedMemoryLabel("");
        setPhase(micOnRef.current ? "listening" : "ready");
        break;
      case "memory.loaded": {
        memoryBoundConversationRef.current = conversationIdRef.current;
        const objectLabel = event.dealTitle ? ` · ${event.dealTitle}` : "";
        setLoadedMemoryLabel(
          `${event.organizationName} · ${event.contactName}${objectLabel}`,
        );
        setNotice(
          event.previousTurnCount
            ? `Алёна получила историю: ${event.previousTurnCount} прошлых реплик.`
            : "Новый контакт: предыдущей истории пока нет.",
        );
        break;
      }
      case "transcript.partial":
        setPartial(event.text);
        break;
      case "transcript.final":
        setPartial("");
        setMessages((current) => [
          ...current,
          { id: makeId(), speaker: "client", text: event.text },
        ]);
        userFinishedAtRef.current = performance.now();
        gotFirstDeltaRef.current = false;
        currentAgentIdRef.current = null;
        setPhase("thinking");
        break;
      case "agent.thinking":
        setPhase("thinking");
        break;
      case "agent.text.delta": {
        if (!gotFirstDeltaRef.current) {
          gotFirstDeltaRef.current = true;
          if (userFinishedAtRef.current !== null) {
            setReactionMs(Math.round(performance.now() - userFinishedAtRef.current));
          }
        }
        const id = currentAgentIdRef.current ?? makeId();
        currentAgentIdRef.current = id;
        setMessages((current) => {
          const existing = current.find((item) => item.id === id);
          if (!existing) {
            return [
              ...current,
              {
                id,
                speaker: "agent",
                text: event.delta,
                voiceVariant: ttsLabelRef.current,
              },
            ];
          }
          return current.map((item) =>
            item.id === id ? { ...item, text: item.text + event.delta } : item,
          );
        });
        break;
      }
      case "agent.text.final":
        if (modeRef.current === "demo" && event.text) speakDemo(event.text);
        if (phaseRef.current !== "speaking") {
          setPhase(micOnRef.current ? "listening" : "ready");
        }
        if (currentAgentIdRef.current) {
          const completedId = currentAgentIdRef.current;
          setMessages((current) =>
            current.map((item) =>
              item.id === completedId ? { ...item, complete: true } : item,
            ),
          );
        }
        currentAgentIdRef.current = null;
        break;
      case "agent.interrupted":
        setInterruptions((value) => value + 1);
        if (currentAgentIdRef.current) {
          const id = currentAgentIdRef.current;
          setMessages((current) =>
            current.map((item) =>
              item.id === id ? { ...item, interrupted: true } : item,
            ),
          );
        }
        currentAgentIdRef.current = null;
        setPhase("listening");
        break;
      case "audio.start":
        ttsRateRef.current = event.sampleRate;
        setPhase("speaking");
        void playerRef.current.start();
        break;
      case "audio.stop":
        playerRef.current.stop();
        window.speechSynthesis.cancel();
        setPhase("listening");
        break;
      case "audio.end":
        setPhase(micOnRef.current ? "listening" : "ready");
        break;
      case "training.correction.ready":
        setFeedbackRecordingId(event.recordingId);
        setFeedbackRecording(true);
        setFeedbackPartial("");
        break;
      case "training.correction.partial":
        setFeedbackPartial(event.text);
        break;
      case "training.correction.final":
        setFeedbackPartial("");
        setFeedbackCorrection((current) =>
          [current.trim(), event.text.trim()].filter(Boolean).join(" "),
        );
        break;
      case "training.correction.saved":
        setFeedbackRecording(false);
        setFeedbackRecordingId(event.recordingId);
        setFeedbackAudioDurationMs(event.durationMs);
        setFeedbackPartial("");
        break;
    }
  };

  const speakDemo = (value: string) => {
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(value);
    utterance.lang = "ru-RU";
    const tuning = audioTuningRef.current;
    utterance.rate = 0.65 + ((tuning.speechSpeed - 1) / 99) * 0.7;
    utterance.volume = tuning.voiceVolume / 100;
    utterance.onstart = () => setPhase("speaking");
    utterance.onend = () => setPhase(micOnRef.current ? "listening" : "ready");
    window.speechSynthesis.speak(utterance);
  };

  const startMicrophone = async (): Promise<boolean> => {
    if (mode === "demo") {
      return true;
    }
    if (captureRef.current) return true;

    try {
      send({ type: "voice.start" });
      const capture = new PcmCapture(
        sttRateRef.current,
        (pcm) => {
          if (socketRef.current?.readyState === WebSocket.OPEN) {
            socketRef.current.send(pcm);
          }
        },
        () => {
          playerRef.current.stop();
          window.speechSynthesis.cancel();
          send({ type: "user.speech_started" });
          setPhase("listening");
        },
        setMicLevel,
        () => phaseRef.current === "speaking",
      );
      capture.setSensitivity(audioTuningRef.current.sensitivity);
      capture.setInterruptionProtection(
        audioTuningRef.current.interruptionProtection,
      );
      await capture.start();
      captureRef.current = capture;
      setIsMicOn(true);
      setPhase("listening");
      setNotice("");
      return true;
    } catch {
      setNotice("Браузер не дал доступ к микрофону. Проверьте разрешение сайта.");
      setPhase("error");
      return false;
    }
  };

  const toggleMicrophone = async () => {
    // Браузеры разрешают вывод звука только после действия пользователя.
    // Открываем канал в самом обработчике клика, а не после асинхронного ответа.
    await playerRef.current.start();
    if (mode === "demo") {
      setNotice("Для микрофона нужен ключ Yandex Cloud. Исходящий сценарий и текстовый тест уже доступны.");
      return;
    }
    if (isMicOn) {
      await captureRef.current?.stop();
      captureRef.current = null;
      setIsMicOn(false);
      setPhase("ready");
      return;
    }
    await startMicrophone();
  };

  const bindSelectedMemory = (conversationId: string) => {
    if (!selectedContactId) return;
    send({
      type: "memory.select",
      conversationId,
      contactId: Number(selectedContactId),
      dealId: selectedDealId ? Number(selectedDealId) : null,
    });
  };

  const beginFreshConversation = () => {
    playerRef.current.stop();
    window.speechSynthesis.cancel();
    send({ type: "session.reset" });
    const conversationId = crypto.randomUUID();
    conversationIdRef.current = conversationId;
    memoryBoundConversationRef.current = null;
    setMessages([]);
    setPartial("");
    setReactionMs(null);
    setInterruptions(0);
    setLoadedMemoryLabel("");
    setConversationReview(null);
    return conversationId;
  };

  const startTestCall = async () => {
    if (!selectedContactId) {
      setNotice("Сначала выберите организацию и контакт — без этого память и обучение не смогут разделять клиентов.");
      return;
    }
    const conversationId = beginFreshConversation();
    await playerRef.current.start();
    if (!(await startMicrophone())) return;
    bindSelectedMemory(conversationId);
    send({ type: "call.start", opening: openingStyle });
  };

  const submitText = (event: FormEvent) => {
    event.preventDefault();
    const value = text.trim();
    if (!value) return;
    if (!selectedContactId) {
      setNotice("Сначала выберите организацию и контакт для этого разговора.");
      return;
    }
    void playerRef.current.start();
    if (
      selectedContactId &&
      memoryBoundConversationRef.current !== conversationIdRef.current
    ) {
      bindSelectedMemory(conversationIdRef.current);
    }
    send({ type: "text.submit", text: value });
    setText("");
  };

  const reset = () => {
    beginFreshConversation();
    setFeedbackMessageId(null);
    setFeedbackTags([]);
    setFeedbackCorrection("");
    setFeedbackRecording(false);
    setFeedbackRecordingId(null);
    setFeedbackPartial("");
    setFeedbackAudioDurationMs(0);
  };

  const createMemoryContext = async () => {
    if (!memoryOrganization.trim() || !memoryContact.trim()) return;
    setMemoryPending(true);
    try {
      const response = await fetch("/api/memory/contexts", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({
          organization_name: memoryOrganization.trim(),
          contact_name: memoryContact.trim(),
          phone: memoryPhone.trim(),
          role: memoryRole.trim(),
          deal_title: memoryDeal.trim(),
        }),
      });
      if (response.status === 401) {
        onSessionExpired();
        return;
      }
      if (!response.ok) throw new Error("memory_create_failed");
      const payload = (await response.json()) as {
        context: { contactId: number; dealId: number | null };
      };
      await loadMemoryContexts();
      setSelectedContactId(String(payload.context.contactId));
      setSelectedDealId(
        payload.context.dealId ? String(payload.context.dealId) : "",
      );
      setMemoryOrganization("");
      setMemoryContact("");
      setMemoryPhone("");
      setMemoryRole("");
      setMemoryDeal("");
      setNotice("Организация и контакт сохранены. Можно начинать первый звонок.");
    } catch {
      setNotice("Не удалось сохранить организацию и контакт.");
    } finally {
      setMemoryPending(false);
    }
  };

  const createDealForSelectedContact = async () => {
    if (!selectedContactId || !newDealTitle.trim()) return;
    setMemoryPending(true);
    try {
      const response = await fetch("/api/memory/deals", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({
          contact_id: Number(selectedContactId),
          title: newDealTitle.trim(),
        }),
      });
      if (!response.ok) throw new Error("deal_create_failed");
      const payload = (await response.json()) as {
        context: { dealId: number | null };
      };
      await loadMemoryContexts();
      setSelectedDealId(payload.context.dealId ? String(payload.context.dealId) : "");
      setNewDealTitle("");
      setNotice("Объект сохранён и выбран для следующего разговора.");
    } catch {
      setNotice("Не удалось сохранить объект.");
    } finally {
      setMemoryPending(false);
    }
  };

  const submitFeedback = async (
    messageId: string,
    rating: "good" | "needs_work",
    tags: string[] = [],
    correction = "",
  ) => {
    const messageIndex = messages.findIndex((item) => item.id === messageId);
    if (messageIndex < 0) return;
    setFeedbackPending(true);
    try {
      const response = await fetch("/api/training/feedback", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({
          conversation_id: conversationIdRef.current,
          agent_message_id: messageId,
          rating,
          tags,
          correction,
          correction_recording_id:
            rating === "needs_work" ? feedbackRecordingId : null,
          note: `voice_variant=${messages[messageIndex].voiceVariant ?? "unknown"}`,
          transcript: messages
            .slice(0, messageIndex + 1)
            .filter((item) => item.speaker === "client" || item.speaker === "agent")
            .map((item) => ({ speaker: item.speaker, text: item.text })),
        }),
      });
      if (response.status === 401) {
        onSessionExpired();
        return;
      }
      if (!response.ok) throw new Error("feedback_failed");
      setMessages((current) =>
        current.map((item) =>
          item.id === messageId ? { ...item, feedback: rating } : item,
        ),
      );
      setFeedbackMessageId(null);
      setFeedbackTags([]);
      setFeedbackCorrection("");
      setFeedbackRecordingId(null);
      setFeedbackPartial("");
      setFeedbackAudioDurationMs(0);
    } catch {
      setNotice("Не удалось сохранить оценку. Попробуйте ещё раз.");
    } finally {
      setFeedbackPending(false);
    }
  };

  const toggleFeedbackTag = (tag: string) => {
    setFeedbackTags((current) =>
      current.includes(tag)
        ? current.filter((item) => item !== tag)
        : [...current, tag],
    );
  };

  const selectTtsEngine = async (engine: string) => {
    if (engine === ttsEngine) return;
    playerRef.current.stop();
    window.speechSynthesis.cancel();
    send({ type: "tts.engine.set", engine });
  };

  const startFeedbackRecording = async () => {
    if (mode === "demo") {
      setNotice("Для голосовой корректировки нужен подключённый речевой сервис.");
      return;
    }
    feedbackStartedMicrophoneRef.current = !micOnRef.current;
    if (!(await startMicrophone())) return;
    const recordingId = crypto.randomUUID();
    setFeedbackCorrection("");
    setFeedbackPartial("");
    setFeedbackAudioDurationMs(0);
    setFeedbackRecordingId(recordingId);
    send({ type: "training.correction.start", recordingId });
  };

  const stopFeedbackRecording = async () => {
    send({ type: "training.correction.stop" });
    setFeedbackRecording(false);
    setFeedbackPartial("");
    if (feedbackStartedMicrophoneRef.current) {
      await captureRef.current?.stop();
      captureRef.current = null;
      setIsMicOn(false);
      setPhase("ready");
      feedbackStartedMicrophoneRef.current = false;
    }
  };

  const bars = useMemo(() => Array.from({ length: 9 }, (_, index) => index), []);
  const selectedMemoryContext = useMemo(
    () =>
      memoryContexts.find(
        (context) => context.contactId === Number(selectedContactId),
      ) ?? null,
    [memoryContexts, selectedContactId],
  );
  const isActive = phase === "listening" || phase === "thinking" || phase === "speaking";

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="brand">
          <span className="brand-mark">ЛМ</span>
          <div>
            <p className="eyebrow">ЛетоМаркет · внутренний стенд</p>
            <h1>ИИ-продажник</h1>
          </div>
        </div>
        <div className="account-actions">
          <div className={`mode-badge ${mode}`}>
            <span className="status-dot" />
            {mode === "live"
              ? ttsOptions.length > 1
                ? "Речевой A/B стенд"
                : "Речевой сервис подключён"
              : "Деморежим"}
          </div>
          <span className="account-name">{user.displayName}</span>
          {user.isAdmin ? (
            <button className="header-button" onClick={onOpenAdmin} type="button">
              Доступы
            </button>
          ) : null}
          <button className="header-button" onClick={onLogout} type="button">
            Выйти
          </button>
        </div>
      </header>

      <main className="workspace">
        <section className="conversation-panel">
          <div className="panel-heading">
            <div>
              <p className="eyebrow">Тестовый разговор</p>
              <h2>{phaseLabels[phase]}</h2>
            </div>
            <button className="ghost-button" onClick={reset} type="button">
              Начать заново
            </button>
          </div>

          <div className="dialog" aria-live="polite">
            {messages.length === 0 && !partial ? (
              <div className="empty-state">
                <span className="empty-index">01</span>
                <h3>Проверьте разговор как настоящий клиент</h3>
                <p>
                  Спросите цену, возразите, перебейте агента или расскажите об объекте.
                  Мы оцениваем не прохождение скрипта, а качество реакции.
                </p>
              </div>
            ) : null}
            {messages.map((message) => (
              <article className={`message ${message.speaker}`} key={message.id}>
                <span>
                  {message.speaker === "client"
                    ? "Вы"
                    : `Агент${message.voiceVariant ? ` · ${message.voiceVariant}` : ""}`}
                </span>
                <p>{message.text}</p>
                {message.interrupted ? <small>Реплика прервана клиентом</small> : null}
                {message.speaker === "agent" && message.complete ? (
                  <div className="feedback-block">
                    {message.feedback ? (
                      <small className="feedback-saved">
                        {message.feedback === "good"
                          ? "Сохранено как удачный ответ"
                          : "Исправление сохранено для обучения"}
                      </small>
                    ) : (
                      <div className="feedback-actions">
                        <button
                          onClick={() => void submitFeedback(message.id, "good")}
                          type="button"
                        >
                          Так можно
                        </button>
                        <button
                          onClick={() => {
                            setFeedbackMessageId(message.id);
                            setFeedbackTags([]);
                            setFeedbackCorrection("");
                            setFeedbackRecording(false);
                            setFeedbackRecordingId(null);
                            setFeedbackPartial("");
                            setFeedbackAudioDurationMs(0);
                          }}
                          type="button"
                        >
                          Надо исправить
                        </button>
                      </div>
                    )}
                    {feedbackMessageId === message.id ? (
                      <div className="feedback-editor">
                        <p>Что было не так?</p>
                        <div className="feedback-tags">
                          {[
                            ["did_not_understand", "Не понял клиента"],
                            ["did_not_answer", "Не ответил"],
                            ["template_phrase", "Шаблонно"],
                            ["too_long", "Слишком долго"],
                            ["weak_sales_move", "Слабо ведёт к продаже"],
                            ["fact_error", "Ошибка в фактах"],
                            ["voice_or_pronunciation", "Голос или произношение"],
                          ].map(([tag, label]) => (
                            <button
                              className={feedbackTags.includes(tag) ? "selected" : ""}
                              key={tag}
                              onClick={() => toggleFeedbackTag(tag)}
                              type="button"
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                        <label>
                          Как лучше ответить
                          <textarea
                            onChange={(event) => setFeedbackCorrection(event.target.value)}
                            placeholder="Напишите своими словами — можно коротко"
                            rows={3}
                            value={feedbackCorrection}
                          />
                        </label>
                        <div className={`voice-feedback ${feedbackRecording ? "recording" : ""}`}>
                          <button
                            onClick={() =>
                              void (feedbackRecording
                                ? stopFeedbackRecording()
                                : startFeedbackRecording())
                            }
                            type="button"
                          >
                            {feedbackRecording
                              ? "Остановить запись"
                              : "Надиктовать исправление"}
                          </button>
                          <p>
                            {feedbackPartial
                              ? `Распознаю: ${feedbackPartial}`
                              : feedbackAudioDurationMs > 0
                                ? `Голос и интонация сохранены · ${Math.max(1, Math.round(feedbackAudioDurationMs / 1000))} сек.`
                                : "Сохраним слова, паузы и голосовые акценты."}
                          </p>
                        </div>
                        <div className="feedback-editor-actions">
                          <button
                            disabled={
                              feedbackPending ||
                              feedbackRecording ||
                              (!feedbackTags.length &&
                                !feedbackCorrection.trim() &&
                                !feedbackRecordingId)
                            }
                            onClick={() =>
                              void submitFeedback(
                                message.id,
                                "needs_work",
                                feedbackTags,
                                feedbackCorrection,
                              )
                            }
                            type="button"
                          >
                            {feedbackPending ? "Сохраняю…" : "Сохранить для обучения"}
                          </button>
                          <button
                            onClick={() => {
                              if (feedbackRecording) void stopFeedbackRecording();
                              setFeedbackMessageId(null);
                              setFeedbackRecordingId(null);
                              setFeedbackPartial("");
                              setFeedbackAudioDurationMs(0);
                            }}
                            type="button"
                          >
                            Отмена
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </article>
            ))}
            {partial ? (
              <article className="message client partial">
                <span>Вы · распознаю</span>
                <p>{partial}</p>
              </article>
            ) : null}
          </div>

          <form className="text-test" onSubmit={submitText}>
            <input
              aria-label="Реплика тестового клиента"
              onChange={(event) => setText(event.target.value)}
              placeholder="Или напишите реплику клиента…"
              value={text}
            />
            <button disabled={!text.trim()} type="submit">
              Отправить
            </button>
          </form>
        </section>

        <aside className="control-panel">
          <div className="memory-card">
            <div className="memory-heading">
              <div>
                <p className="eyebrow">Долговременная память</p>
                <h3>Кому звоним</h3>
              </div>
              {selectedMemoryContext ? (
                <span className="memory-counter">
                  {selectedMemoryContext.conversationCount} разг.
                </span>
              ) : null}
            </div>

            <label>
              <span>Организация и контакт</span>
              <select
                onChange={(event) => {
                  setSelectedContactId(event.target.value);
                  setSelectedDealId("");
                  memoryBoundConversationRef.current = null;
                  setLoadedMemoryLabel("");
                  setConversationReview(null);
                }}
                value={selectedContactId}
              >
                <option value="">Выберите контакт для теста</option>
                {memoryContexts.map((context) => (
                  <option key={context.contactId} value={context.contactId}>
                    {context.organizationName} — {context.contactName}
                  </option>
                ))}
              </select>
            </label>

            {selectedMemoryContext ? (
              <>
                <label>
                  <span>Сделка или объект</span>
                  <select
                    onChange={(event) => {
                      setSelectedDealId(event.target.value);
                      memoryBoundConversationRef.current = null;
                      setLoadedMemoryLabel("");
                    }}
                    value={selectedDealId}
                  >
                    <option value="">Общая история контакта</option>
                    {selectedMemoryContext.deals.map((deal) => (
                      <option key={deal.id} value={deal.id}>
                        {deal.title}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="new-deal-row">
                  <input
                    aria-label="Новый объект"
                    onChange={(event) => setNewDealTitle(event.target.value)}
                    placeholder="Например: терраса в Сосновке"
                    value={newDealTitle}
                  />
                  <button
                    disabled={memoryPending || !newDealTitle.trim()}
                    onClick={() => void createDealForSelectedContact()}
                    type="button"
                  >
                    + Объект
                  </button>
                </div>
                <p className="memory-status">
                  {loadedMemoryLabel
                    ? `Загружено: ${loadedMemoryLabel}`
                    : selectedMemoryContext.conversationCount
                      ? "Нажмите «Начать повторный звонок» — Алёна получит предыдущую историю."
                      : "Это будет первый сохранённый разговор с контактом."}
                </p>
              </>
            ) : null}

            <details className="memory-create">
              <summary>Добавить организацию и контакт</summary>
              <div className="memory-create-grid">
                <input
                  onChange={(event) => setMemoryOrganization(event.target.value)}
                  placeholder="Организация"
                  value={memoryOrganization}
                />
                <input
                  onChange={(event) => setMemoryContact(event.target.value)}
                  placeholder="Имя контакта"
                  value={memoryContact}
                />
                <input
                  onChange={(event) => setMemoryPhone(event.target.value)}
                  placeholder="Телефон"
                  value={memoryPhone}
                />
                <input
                  onChange={(event) => setMemoryRole(event.target.value)}
                  placeholder="Роль: снабжение, прораб…"
                  value={memoryRole}
                />
                <input
                  onChange={(event) => setMemoryDeal(event.target.value)}
                  placeholder="Первый объект — необязательно"
                  value={memoryDeal}
                />
                <button
                  disabled={
                    memoryPending ||
                    !memoryOrganization.trim() ||
                    !memoryContact.trim()
                  }
                  onClick={() => void createMemoryContext()}
                  type="button"
                >
                  {memoryPending ? "Сохраняю…" : "Сохранить и выбрать"}
                </button>
              </div>
            </details>
          </div>

          <div className="voice-card">
            <div className={`voice-orb ${isActive ? "active" : ""} ${phase}`}>
              <div className="waveform" aria-hidden="true">
                {bars.map((bar) => (
                  <i
                    key={bar}
                    style={{
                      height: `${12 + Math.max(micLevel * 38, isActive ? (bar % 4) * 5 : 0)}px`,
                    }}
                  />
                ))}
              </div>
            </div>
            <p className="phase-label">{phaseLabels[phase]}</p>
            {ttsOptions.length > 1 ? (
              <div className="voice-ab-field">
                <span>Вариант озвучки</span>
                <div className="voice-ab-buttons">
                  {ttsOptions.map((option) => (
                    <button
                      className={option.id === ttsEngine ? "selected" : ""}
                      disabled={phase === "thinking" || phase === "speaking"}
                      key={option.id}
                      onClick={() => void selectTtsEngine(option.id)}
                      type="button"
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                <small>Имена систем скрыты, чтобы оценивать только звучание.</small>
              </div>
            ) : null}
            <details className="agent-settings" open>
              <summary>
                <span>Настройки Алёны</span>
                <small>точность 1–100</small>
              </summary>
              <div className="agent-settings-body">
                {tuningControls.map((control) => (
                  <label className="agent-range" key={control.key}>
                    <span className="agent-range-heading">
                      <strong>{control.label}</strong>
                      <output>{audioTuning[control.key]}%</output>
                    </span>
                    <input
                      aria-label={control.label}
                      max="100"
                      min="1"
                      onChange={(event) =>
                        setAudioTuning((current) => ({
                          ...current,
                          [control.key]: Number(event.target.value),
                        }))
                      }
                      step="1"
                      type="range"
                      value={audioTuning[control.key]}
                    />
                    <small>{control.hint}</small>
                  </label>
                ))}
                <button
                  className="settings-reset"
                  onClick={() => setAudioTuning(defaultAudioTuning)}
                  type="button"
                >
                  Вернуть базовые настройки
                </button>
              </div>
            </details>
            <label className="opening-field">
              <span>Начало разговора</span>
              <select
                onChange={(event) =>
                  setOpeningStyle(event.target.value as "partnership" | "project")
                }
                value={openingStyle}
              >
                <option value="partnership">Прямо о партнёрстве</option>
                <option value="project">Через вопрос о террасах</option>
              </select>
            </label>
            <button
              className="primary-button"
              disabled={!selectedContactId}
              onClick={() => void startTestCall()}
              type="button"
            >
              {selectedMemoryContext?.conversationCount
                ? "Начать повторный звонок"
                : "Начать тестовый звонок"}
            </button>
            <button
              className={`secondary-button ${isMicOn ? "stop" : ""}`}
              onClick={() => void toggleMicrophone()}
              type="button"
            >
              {isMicOn ? "Остановить микрофон" : "Начать голосовой тест"}
            </button>
            <p className="control-hint">
              {mode === "live"
                ? "Алёна начнёт первой. Говорите естественно — её можно перебивать."
                : "Алёна начнёт первой; голос из браузера доступен уже в деморежиме."}
            </p>
          </div>

          {notice ? <div className="notice">{notice}</div> : null}

          <div className="metrics-card">
            <div className="metric">
              <span>Начало ответа</span>
              <strong>{reactionMs === null ? "—" : `${reactionMs} мс`}</strong>
            </div>
            <div className="metric">
              <span>Перебивания</span>
              <strong>{interruptions}</strong>
            </div>
            <div className="metric full">
              <span>Контур</span>
              <strong>
                {mode === "live"
                  ? ttsOptions.length > 1
                    ? `Один мозг · ${ttsLabel}`
                    : "Распознавание + ИИ + голос"
                  : "Интерфейсный демо"}
              </strong>
            </div>
          </div>

          {conversationReview ? (
            <div className={`review-card ${conversationReview.outcome}`}>
              <div className="review-heading">
                <span>Авторазбор разговора</span>
                <strong>{conversationReview.score}/100</strong>
              </div>
              {conversationReview.summary ? <p>{conversationReview.summary}</p> : null}
              {conversationReview.issues.length ? (
                <ul>
                  {conversationReview.issues.slice(0, 5).map((issue, index) => (
                    <li key={`${issue.code}-${index}`}>{issue.detail || issue.code}</li>
                  ))}
                </ul>
              ) : (
                <small>Критических ошибок на текущем этапе не найдено.</small>
              )}
            </div>
          ) : null}

          <div className="principle-card">
            <p className="eyebrow">Главное правило</p>
            <p>Сначала ответить на вопрос клиента. Затем дать ему отреагировать.</p>
          </div>
        </aside>
      </main>
    </div>
  );
}
